/* eslint-disable */
import { useMemo } from "react";
import { useDictionary, useOLAPTransformer, OLAPTransformerResult, OLAPTransformerOptions } from "../../../shared";
import { dictionaryService } from "../../../dataFlow";
import { Dictionary } from "@sa/rep-mdm";
import { OLAPResult } from "@sa/rep-olap";
import { SLIDE_NUMBER } from "../constants";

const ROW_AXIS = "calendar.default";
const UNITS_AXIS = "units.default";
const COLUMN_AXIS = "commodities.default";

export function useChartData(olapResult: OLAPResult | null): OLAPTransformerResult | null {
  const [, rowsDict] = useDictionary(dictionaryService, "calendar-origin");
  const [itemsColumns, columnsDict] = useDictionary(dictionaryService, "slides-commodities");
  const [commoditiesColumns] = useDictionary(dictionaryService, "v_dict_commodities_alternative_price", undefined, {
    attributes: "",
    slide: SLIDE_NUMBER,
  });
  const [unitsColumns, unitsDict] = useDictionary(dictionaryService, "v_dict_units_alternative_price", undefined, {
    attributes: "",
    slide: SLIDE_NUMBER,
  });

  const options = useMemo<OLAPTransformerOptions | null>(() => {
    if ([olapResult, columnsDict, rowsDict, itemsColumns, unitsDict, commoditiesColumns].some((x) => !Boolean(x))) {
      return null;
    }

    return {
      data: olapResult?.data,
      attrsNameProxy: new Map([
        [
          `${COLUMN_AXIS}.name`,
          (id: unknown, lcid: number) => {
            const itemDisplay = commoditiesColumns?.find((x) => Number(x.getValue("id")) === Number(id));
            const item = itemsColumns?.find((x) => Number(x.id) === Number(id));

            return (itemDisplay || item)?.getValue("name") || { [lcid]: "" };
          },
        ],
        [
          `${COLUMN_AXIS}.id`,
          (id: unknown, lcid: number) => {
            const itemDisplay = commoditiesColumns?.find((x) => Number(x.getValue("id")) === Number(id));
            const item = itemsColumns?.find((x) => Number(x.id) === Number(id));

            return (itemDisplay || item)?.getValue("id") || id;
          },
        ],
        [
          `${UNITS_AXIS}.name`,
          (id: unknown, lcid: number) => {
            const itemDisplay = unitsColumns?.find((x) => Number(x.getValue("id")) === Number(id));

            return itemDisplay?.getValue("name") || { [lcid]: "" };
          },
        ],
      ]),
      attrsDictionaries: new Map([
        [COLUMN_AXIS, columnsDict],
        [ROW_AXIS, rowsDict],
        [UNITS_AXIS, unitsDict],
      ]) as ReadonlyMap<string, Dictionary>,
    } as OLAPTransformerOptions;
  }, [olapResult, columnsDict, rowsDict, itemsColumns, commoditiesColumns]);

  const query = useMemo(() => {
    return {
      lcid: 9,
      columns: [`${COLUMN_AXIS}.name`, `${COLUMN_AXIS}.id`, `${UNITS_AXIS}.id`, `${UNITS_AXIS}.name`],
      rows: [`${ROW_AXIS}.name`, `${ROW_AXIS}.id`, `${UNITS_AXIS}.id`, `${UNITS_AXIS}.name`],
      cellData: {
        inCell: ["Measures.value"],
        attributes: [
          `${COLUMN_AXIS}.name`,
          `${ROW_AXIS}.name`,
          `${COLUMN_AXIS}.id`,
          `${ROW_AXIS}.id`,
          `${UNITS_AXIS}.id`,
          `${UNITS_AXIS}.name`,
        ],
      },
    };
  }, []);
  const data = useOLAPTransformer(options, query);
  return data;
}
